import { useMemo } from 'react';

import { useListContext } from 'react-admin';
import { FormProvider } from 'react-hook-form';
import { FilterContextProvider } from 'shared/mui/Filter/context/filterContext';
import {
  FilterApplyButton,
  FilterButton,
  FilterClearButton,
  FilterResetButton,
} from 'shared/mui/Filter/FilterButtons';
import { FilterColumnsButton } from 'shared/mui/Filter/FilterButtons';
import { FilterChipsToolbar } from 'shared/mui/Filter/FilterChipsToolbar/FilterChipsToolbar';
import {
  FilterDrawer,
  FilterDrawerActionsButtons,
  FilterDrawerContent,
} from 'shared/mui/Filter/FilterDrawer';
import { FilterToolbar } from 'shared/mui/Filter/FilterToolbar/FilterToolbar';
import { useFilters } from 'shared/mui/Filter/hooks/useFilters';
import { useSyncFilter } from 'shared/mui/Filter/hooks/useSyncFilter';
import { cleanEmpty } from 'utils/cleanEmpty';

import { PresetsListFilterChips } from './PresetsListFilterChips';
import { PresetsListFilterForm } from './PresetsListFilterForm';

type PresetsListFilterValues = {
  merchant: string | null;
  gateway: string[];
  currency: string[];
  presetItems: {
    direction: string | null;
  };
};

export const PresetsListFilters: React.FC = () => {
  const { filterValues, data } = useListContext();

  const { form, appliedFilters, setAppliedFilter, onSubmit, onReset, openDrawer, toggleDrawer } =
    useFilters<PresetsListFilterValues>({
      mode: 'all',
      defaultValues: {
        merchant: filterValues?.merchant || null,
        gateway: filterValues?.gateway || [],
        currency: filterValues?.currency || [],
        presetItems: {
          direction: filterValues?.presetItems?.direction || null,
        },
      },
      resetValues: {
        merchant: null,
        gateway: [],
        currency: [],
        presetItems: {
          direction: null,
        },
      },
    });

  const listFilters = useMemo(() => cleanEmpty({ ...appliedFilters }), [appliedFilters]);
  const isListFilterExist = Object.keys(listFilters).length > 0;

  useSyncFilter<PresetsListFilterValues>({ appliedFilters });

  return (
    <>
      <FilterToolbar
        leftActionsSlot={[
          <FilterButton key="filter" onClick={toggleDrawer(true)} />,
          <FilterColumnsButton disabled={!data?.length} key="select-column" />,
        ]}
      />
      <FilterDrawer onClose={toggleDrawer(false)} open={openDrawer}>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FilterDrawerContent>
              <PresetsListFilterForm />
            </FilterDrawerContent>
            <FilterDrawerActionsButtons>
              <FilterResetButton label="Очистить" onReset={onReset} />
              <FilterApplyButton type="submit" />
            </FilterDrawerActionsButtons>
          </form>
        </FormProvider>
      </FilterDrawer>
      <FilterChipsToolbar appliedFilters={appliedFilters}>
        <FilterContextProvider appliedFilters={appliedFilters} setAppliedFilter={setAppliedFilter}>
          <FormProvider {...form}>
            <PresetsListFilterChips />
            <FilterClearButton key="clear-filter" onClick={onReset} visible={isListFilterExist} />
          </FormProvider>
        </FilterContextProvider>
      </FilterChipsToolbar>
    </>
  );
};
